import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ListFilterBar } from '@/components/ListFilterBar';
import { useListFilters } from '@/lib/useListFilters';
import { useFilterOptions } from '@/lib/useFilterOptions';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate, formatNumber } from '@/i18n/format';
import type { InboundRow } from '@/api/types';
import { RevisionMark } from '@/components/RequestRevisionNotice';
import { RequirePermission } from '@/components/RequirePermission';

const LIMIT = 50;
const STATUSES = ['EXPECTED', 'ARRIVED', 'RECEIVING', 'RECEIVED', 'CLOSED', 'CANCELLED'];

export function InboundsListPage(): JSX.Element {
  const [params] = useSearchParams();
  const [discrepanciesOnly, setDiscrepanciesOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const list = useListFilters(() => setOffset(0));
  // Deep link from Overview's "Open inbounds" card: same server-side filter the card counts with (not combined with an explicit status).
  const openOnly = params.get('openOnly') === 'true' && list.filters.status === '';
  const result = useApi(
    () => endpoints.inbounds({ ...list.query, openOnly: openOnly || undefined, discrepanciesOnly, limit: LIMIT, offset }),
    [list.key, openOnly, discrepanciesOnly, offset],
  );
  const options = useFilterOptions();
  const { t, locale } = useLanguage();

  const columns: readonly Column<InboundRow>[] = [
    { key: 'number', header: t('inbounds.colNumber'), render: (r) => <>{r.number}<RevisionMark revisionNo={r.revision_no} /></> },
    { key: 'owner', header: t('inbounds.colSeller'), render: (r) => r.owner_name },
    { key: 'warehouse', header: t('inbounds.colWarehouse'), render: (r) => r.warehouse_name },
    { key: 'state', header: t('common.status'), render: (r) => <StatusBadge value={r.state} /> },
    { key: 'created', header: t('filters.created'), render: (r) => (r.created_at ? formatDate(r.created_at, locale) : '—') },
    { key: 'expected', header: t('inbounds.colExpected'), render: (r) => (r.expected_at ? formatDate(r.expected_at, locale) : '—') },
    {
      key: 'received',
      header: t('inbounds.colReceived'),
      render: (r) => `${formatNumber(r.qty_received, locale)} / ${formatNumber(r.qty_expected, locale)}`,
      align: 'right',
    },
    {
      key: 'awaiting',
      header: t('inbounds.colAwaitingPutaway'),
      // derived from the receiving-zone stock - the document status itself (RECEIVED / CLOSED) is untouched
      render: (r) => (Number(r.awaiting_putaway ?? 0) > 0 ? <span className="badge warn">{formatNumber(r.awaiting_putaway!, locale)}</span> : '—'),
      align: 'right',
    },
    {
      key: 'discrepancies',
      header: t('inbounds.colDiscrepancies'),
      render: (r) => (Number(r.discrepancy_count) > 0 ? <span className="badge danger">{formatNumber(r.discrepancy_count, locale)}</span> : '0'),
      align: 'right',
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.inbounds')}</h1>
        <RequirePermission permission="RECEIVING.WRITE">
          <Link className="button-link" to="/inbounds/new">
            {t('inbounds.newInbound')}
          </Link>
        </RequirePermission>
      </div>
      <ListFilterBar
        filters={list.filters}
        onChange={list.set}
        onReset={list.reset}
        filtered={list.filtered}
        statuses={STATUSES.map((s) => ({ value: s, label: statusLabel(t, s) }))}
        owners={options.sellers}
        warehouses={options.warehouses}
      />
      <div className="filters">
        <label>
          <input
            type="checkbox"
            checked={discrepanciesOnly}
            onChange={(e) => {
              setDiscrepanciesOnly(e.target.checked);
              setOffset(0);
            }}
          />
          {t('inbounds.discrepanciesOnly')}
        </label>
      </div>
      <DataTable
        result={result}
        columns={columns}
        getRowHref={(r) => `/inbounds/${r.id}`}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={list.filtered ? t('filters.noMatch') : t('inbounds.emptyTitle')}
      />
    </div>
  );
}
